import {
  generateId,
  getSystemFields,
  isEmpty,
  getIdFromString,
  removeSpecialCharacters
} from "../libs/utils";
import { FieldTypes } from "./field.types";
import { COLUMN_TYPES, FLOW_TYPE, MODEL_TO_PARENT_MODEL } from "../constants";
import { validateObject } from "./cleanup";

const PHASE_TWO_COPY_EXCLUDE_MAPPING = {
  Model: {
    oneToMany: ["Model::ProcessDef"]
  },
  Column: {
    oneToMany: ["Column::Appearance", "ColumnVisibility::Criteria"]
  },
  Field: {
    oneToMany: [
      "FieldValidation::Criteria",
      "Field::QueryDefinition",
      "Field::Expression",
      "Field::Property"
    ]
  }
};

const PHASE_THREE_COPY_EXCLUDE_MAPPING = {
  Column: {
    oneToMany: ["Column::Permission", "ActivityColumn::Appearance"]
  }
};

const getExcludeMappingList = (childKind) => {
  let phaseTwoExcludeList = PHASE_TWO_COPY_EXCLUDE_MAPPING[childKind]
    ? PHASE_TWO_COPY_EXCLUDE_MAPPING[childKind].oneToMany
    : [];

  let phaseThreeExcludeList = PHASE_THREE_COPY_EXCLUDE_MAPPING[childKind]
    ? PHASE_THREE_COPY_EXCLUDE_MAPPING[childKind].oneToMany
    : [];
  return [...phaseTwoExcludeList, ...phaseThreeExcludeList];
};

const GRAND_RELATIONSHIPS = {
  Node: "FieldModel",
  RefreshField: "Model"
};

let oldNewModelInsMapping = new Map();
let oldNewFieldMapping = {};
let expressionList = [];

class BaseModel {
  constructor(ds, id, context) {
    this.id = id;
    this.ds = ds;
    this.context = context;
    // this.dataSet = jsonData;
    // this.currentJson = jsonData[id];
  }

  static classes = {}; //all metadata class against its name mapping
  static fieldTypes = {}; //holds the respective fields and its data type
  /*
    Holds the additional many to one reference list, Field is parent to Column and Model
    In which Column will be a direct parent but Model is also a cascade relation to Field
    So we need to maintain this list to order to update Model's one to many
    while creating Field via Column, as well remove from Model's one to many while
    deleting a Field.
  */
  static manyToOneList = []; //holds foreign key
  static oneToManyList = []; //holds the cascade/composition one to many key list

  //should only be called from the root model eg: Form/Process/Project/Case Models
  static registerModel(model, modelName) {
    modelName = modelName || model.name;
    //creating a map for className and class
    this.classes = Object.assign({}, this.classes, {
      [modelName]: model
    });
  }

  static setFieldTypes(fields) {
    this.fieldTypes = Object.assign(fields, this.fieldTypes);
  }

  static getMetadata(kind, type) {
    let modelClass = this.classes[type + kind];
    if (modelClass) {
      return modelClass;
    }

    let defaultModelClass = this.classes[kind];

    if (!defaultModelClass) {
      throw new Error(`Metadata definition for '${kind}' is not found`);
    }
    return defaultModelClass;
  }

  static setOneToManyList(oneToManyList) {
    this.oneToManyList = this.oneToManyList || [];
    this.oneToManyList = this.oneToManyList.concat(oneToManyList);
  }

  static setManyToOneList(manyToOneList) {
    this.manyToOneList = this.manyToOneList || [];
    this.manyToOneList = this.manyToOneList.concat(manyToOneList);
  }

  static reset() {
    this.classes = {};
  }

  static parentModelFields = [];

  get currentJson() {
    return this.ds.get(this.id);
  }

  get dataSet() {
    return this.ds.getAll();
  }

  // get id() {
  //   return this.getValue('Id');
  // }

  get kind() {
    return this.getValue("Kind");
  }

  toJSON() {
    return Object.assign({}, this.currentJson);
  }

  getMetadata(kind, instanceObject = {}) {
    let type = null;
    if (instanceObject?.Widget && kind === COLUMN_TYPES.FIELD) {
      type = instanceObject.Widget;
    } else {
      type = instanceObject?.Type;
    }
    return this.context.getMetadata(kind, type);
  }

  getSystemFields(flowType = FLOW_TYPE.PROCESS) {
    const currentUser = {
      Id: "_current_user",
      Name: "Current User",
      Type: "User"
    };
    getSystemFields(MODEL_TO_PARENT_MODEL[flowType], flowType)
      .then((systemFields) => {
        BaseModel.parentModelFields = systemFields.map((field) => field.Id);
        if (!BaseModel.parentModelFields.includes(currentUser.Id)) {
          BaseModel.parentModelFields.push(currentUser.Id);
        }
      })
      .catch((err) => {
        console.error(
          "Failed to get system fields, results prone to error",
          err
        );
      });
    //getSystemFields(SYSTEM_MODELS.USER);

    return BaseModel.parentModelFields;
  }

  validateCreate(kind, params, refName) {
    this.validateOneToManyOnCreate(kind, refName);
    this.validateRequiredPropsOnCreate(kind, params);
  }

  validateOneToManyOnCreate(kind, refName) {
    let childName = refName + "::" + kind;
    if (this.constructor.oneToManyList.indexOf(childName) === -1) {
      throw new Error(
        `Unknown child name '${kind}' for the metadata ${refName}`
      );
    }
  }

  validateRequiredPropsOnCreate(kind, params) {
    let constructor = this.getMetadata(kind, params);
    //setting default value as null to all fields, which will help in identifying the required fields TODO
    var args = {};
    for (let name in constructor.fieldTypes) {
      if (constructor.fieldTypes[name].required) {
        args[name] = null;
      }
    }
    Object.assign(args, params);
    for (let name in args) {
      this.validateUpdate(name, args[name], constructor);
    }
  }

  validateUpdate(name, value, constructor = this.constructor) {
    let fieldType = constructor.fieldTypes[name];
    if (fieldType) {
      fieldType(name, value);
    } else {
      throw new Error(
        `Undefined field name '${name}' for the metadata ${constructor.name}`
      );
    }
  }

  validateGet(name) {
    if (!constructor.fieldTypes[name]) {
      throw new Error(
        `Undefined field name '${name}' for the metadata ${constructor.name}`
      );
    }
  }

  getModel(modelId = this.dataSet["Root"]) {
    return this.getObject(modelId);
  }

  getObject(id) {
    if (!this.dataSet[id]) {
      return null;
    }
    if (this.ds.cache.get(id)) {
      return this.ds.cache.get(id);
    }
    let kind = this.dataSet[id].Kind;

    var dynamicClass = this.getMetadata(kind, this.dataSet[id]);

    let ins = new dynamicClass(this.ds, id, this.context);
    this.ds.cache.set(id, ins);
    return ins;
  }

  createObject(name, params = {}) {
    let id = params["Id"] || generateId(name);
    //this does not checks the case sensitive
    if (this.dataSet[id]) {
      throw new Error(
        `Error while creating ${name}, the given Id ${id} already exist`
      );
    }
    let args = { Id: id, Kind: name };
    if (params.Type) {
      args["Type"] = params.Type;
    }
    if (params.Widget) {
      args["Widget"] = params.Widget;
    }
    //temp fix for isChildModel check
    if (name === COLUMN_TYPES.MODEL && params.Model) {
      args["Model"] = params.Model;
    }
    this.dataSet[id] = args;
    // this.ds.create(id, args);
    Reflect.deleteProperty(params, "Id");
    return this.getObject(id);
  }

  getValue(name) {
    let val = this.currentJson[name];
    return val;
  }

  get(name) {
    let val = this.currentJson[name];
    if (val == null) {
      return null;
    }
    let fieldType = this.constructor.fieldTypes[name];
    if (fieldType && fieldType.isReference()) {
      return this.getObject(val);
    } else {
      throw new Error(`The field ${name} is not a reference field`);
    }
  }

  getList(refName, childName) {
    let val = this.currentJson[refName + "::" + childName];
    if (val instanceof Array) {
      let childList = [];
      for (let i in val) {
        let objValue = this.getObject(val[i]);
        if (objValue !== null) {
          childList.push(objValue);
        } else {
          validateObject(this.currentJson, this.dataSet);
        }
      }
      return childList;
    }
    return [];
  }

  updateMulti(args = {}) {
    for (let name in args) {
      let value = args[name];
      this.update(name, value);
    }
    return this;
  }

  update(name, value) {
    //Should not update Id and Kind
    if (["Id", "Kind"].includes(name)) {
      throw new Error(
        `Cannot update property ${name} for model ${this.kind} after creation`
      );
    }
    this.validateUpdate(name, value);
    this.updateOneToMany(name, value);
    const isValueEmpty = (value) => {
      if (value === null) return true;
      else if (typeof value === "string" && value.length === 0) return true;
      return false;
    };
    isValueEmpty(value)
      ? Reflect.deleteProperty(this.currentJson, name)
      : (this.currentJson[name] = value);
    return this;
  }

  updateIndex(childName, childId, index, refName = this.currentJson.Kind) {
    let oneToManyName = refName + "::" + childName;
    let childList = this.currentJson[oneToManyName];
    let originalIndex = childList.indexOf(childId);
    if (originalIndex < index) {
      childList.splice(index, 0, childId);
      childList.splice(originalIndex, 1);
    } else if (originalIndex > index) {
      childList.splice(originalIndex, 1);
      childList.splice(index, 0, childId);
    }
    return this;
  }

  createAt(childName, index, params = {}, refName = this.currentJson.Kind) {
    let newObj = this.create(childName, params, refName);
    this.updateIndex(childName, newObj.id, index);
    return newObj;
  }

  create(childName, params = {}, refName = this.currentJson.Kind) {
    params[refName] = this.id;
    this.validateCreate(childName, params, refName);

    let newObj = this.createObject(childName, params);
    newObj.updateMulti(params);
    return newObj;
  }

  delete(
    childName,
    id,
    refName = this.currentJson.Kind,
    skipOnError = window.skipOnError
  ) {
    let oneToManyName = refName + "::" + childName;
    var childList = this.currentJson[oneToManyName] || [];
    let index = childList.indexOf(id);
    if (index === -1) {
      console.warn(
        `trying to delete child property ${oneToManyName} with id ${id} that does not exists`
      );
      return;
    }

    this.deleteOnetoMany(id, refName, skipOnError);

    childList.splice(index, 1);
    //after removing that entry from one to many, if its blank clear the onetomany prop
    if (childList.length === 0) {
      Reflect.deleteProperty(this.currentJson, oneToManyName);
    }
  }

  updateOneToMany(name, nextValue) {
    let referenceList = this.constructor.manyToOneList;
    if (referenceList.find((refName) => refName.indexOf(`${name}::`) === 0)) {
      // console.info("Updating Reference ", this.id, "['", name, "'] = ", nextValue);
      let prevValue = this.currentJson[name];
      let oneToManyName = name + "::" + this.currentJson.Kind;
      //removing the prevValue's onetomany
      if (
        prevValue &&
        BaseModel.parentModelFields.includes(prevValue) === false
      ) {
        // Block removing oneToMany reference for systemFields if it is a field
        var prevReferredObj = this.dataSet[prevValue];
        if (prevReferredObj) {
          // console.info("Removing the", this.id, "from", prevValue, "['", oneToManyName, "']");
          let childList = prevReferredObj[oneToManyName] || [];
          let index = childList.indexOf(this.id);
          if (index > -1) {
            childList.splice(index, 1);
            //after removing that entry from one to many, if its blank clear the onetomany prop
            if (childList.length === 0) {
              Reflect.deleteProperty(prevReferredObj, oneToManyName);
            }
          } else {
            console.warn(
              `Trying to remove prev value ${prevValue} in ${this.id} from ${oneToManyName} which is not found`
            );
          }
        } else {
          throw new Error(`Error while updating the Previous Reference ${name}
            with the value ${prevValue} in ${this.id}`);
        }
      }
      //updating the nextValue's onetomany
      if (
        nextValue &&
        BaseModel.parentModelFields.includes(nextValue) === false
      ) {
        // Block adding oneToMany reference for systemFields if it is a field
        var referredObj = this.dataSet[nextValue];
        if (referredObj) {
          var childList = referredObj[oneToManyName] || [];
          // console.info("Updating ", this.id, "in", nextValue, "['", oneToManyName, "']");
          referredObj[oneToManyName] = childList;
          childList.push(this.id);
        } else {
          throw new Error(
            `Error while updating the Next Reference ${name} with the value ${nextValue} in ${this.id}`
          );
        }
      }
    }
  }

  deleteOnetoMany(id, viaRefName, skipOnError = false) {
    let toBeDeleted = this.dataSet[id];
    if (!toBeDeleted) {
      console.warn(
        `trying to delete a child of ${viaRefName} with id ${id} is not in model`
      );
      return;
    }
    let kind = toBeDeleted.Kind;
    console.info("Deleteting ", id, " of the kind ", kind);

    let constructor = this.getMetadata(kind, toBeDeleted);

    //Removes the complete one to many objects
    for (let oneToManyName of constructor.oneToManyList) {
      let oneToManyList = toBeDeleted[oneToManyName] || [];
      let refName = oneToManyName.split("::")[0];
      for (let childId of oneToManyList) {
        this.deleteOnetoMany(childId, refName, skipOnError);
      }
    }

    //Removes the one to many reference in additional parent
    for (let manyToOneName of constructor.manyToOneList) {
      manyToOneName = manyToOneName.split("::")[0];
      let manyToOneId = toBeDeleted[manyToOneName];
      // Block adding oneToMany reference for systemFields if it is a field
      if (BaseModel.parentModelFields.includes(manyToOneId)) {
        continue;
      }
      //skipping the parent reference, because parent obj it will anyway get deleted at end
      if (manyToOneId && manyToOneName !== viaRefName) {
        let manyToOneObj = this.dataSet[manyToOneId];
        let oneToManyName = manyToOneName + "::" + kind;
        if (!manyToOneObj) {
          console.warn(
            `Object not found for id ${manyToOneId} but it is being referred in model ${this.id}`
          );
          if (skipOnError) {
            continue;
          }
        }
        console.info(
          id,
          manyToOneId,
          "['",
          oneToManyName,
          "']",
          manyToOneObj[oneToManyName]
        );
        var childList = manyToOneObj[oneToManyName] || [];
        let index = childList.indexOf(id);
        if (index > -1) {
          childList.splice(index, 1);
          if (childList.length === 0) {
            Reflect.deleteProperty(manyToOneObj, oneToManyName);
          }
        } else {
          throw new Error(
            `The ${kind} ${id} not found in the one to many list of ${manyToOneName}[${oneToManyName}]`
          );
        }
      }
    }
    Reflect.deleteProperty(this.dataSet, id);
  }

  copy(childObj, index, refName = this.currentJson.Kind) {
    let newChildObj = this.copyChild(childObj, refName);
    if (index > -1) {
      this.updateIndex(newChildObj.currentJson.Kind, newChildObj.id, index);
    }
    this.copyPhaseTwo();
    this.copyPhaseThree();

    expressionList.forEach((exp) => {
      let expInstance = this.getObject(exp);
      let expressionStr = expInstance.getExpressionStr();
      expInstance.update("ExpressionStr", expressionStr);
    });

    //clear the cache
    oldNewModelInsMapping.clear();
    oldNewFieldMapping = {};
    expressionList = [];
    return newChildObj;
  }

  copyPhaseTwo() {
    console.info("*****running phase two of copy******");
    let oldModelObjects = oldNewModelInsMapping.entries();
    for (let [oldModelObj, newModelObj] of oldModelObjects) {
      let oldModelKind = oldModelObj.currentJson.Kind;
      let excludedChildList = PHASE_TWO_COPY_EXCLUDE_MAPPING[oldModelKind]
        ? PHASE_TWO_COPY_EXCLUDE_MAPPING[oldModelKind].oneToMany
        : [];

      excludedChildList.forEach((oneToManyKey) => {
        let [refName, childName] = oneToManyKey.split("::");
        let childList = oldModelObj.getList(refName, childName);
        for (let child of childList) {
          newModelObj.copyChild(child, refName);
        }
      });
    }
  }

  copyPhaseThree() {
    console.info("*****running phase three of copy******");
    let oldModelObjects = oldNewModelInsMapping.entries();
    for (let [oldModelObj, newModelObj] of oldModelObjects) {
      let oldModelKind = oldModelObj.currentJson.Kind;
      let excludedChildList = PHASE_THREE_COPY_EXCLUDE_MAPPING[oldModelKind]
        ? PHASE_THREE_COPY_EXCLUDE_MAPPING[oldModelKind].oneToMany
        : [];

      excludedChildList.forEach((oneToManyKey) => {
        let [refName, childName] = oneToManyKey.split("::");
        let childList = oldModelObj.getList(refName, childName);
        for (let child of childList) {
          newModelObj.copyChild(child, refName);
        }
      });
    }
  }

  copyChild(childObj, refName = this.currentJson.Kind) {
    let oneToManyList = [];
    let newChildProps = {};
    let childKind = childObj.currentJson.Kind;

    let oneToManyKeyList = this.getMetadata(
      childKind,
      childObj.currentJson
    ).oneToManyList;
    let manyToOneKeyList = this.getMetadata(
      childKind,
      childObj.currentJson
    ).manyToOneList;
    console.info(
      `**** coping ${childKind} into ${refName} childList ${oneToManyKeyList} ****`
    );
    Object.keys(childObj.currentJson).forEach((key) => {
      if (key.indexOf("::") !== -1) {
        // Take only a child key property and ignoring all many to one key
        let excludeChildList = getExcludeMappingList(childKind);
        if (
          oneToManyKeyList.includes(key) === true &&
          excludeChildList.includes(key) === false
        ) {
          oneToManyList.push(key);
        }
      } else if (key !== "Id" && key !== "Kind") {
        // skip name changing for button columns & Activity
        if (
          childObj.currentJson["Kind"] === "Column" &&
          childObj.currentJson["Type"] !== "Button" &&
          (key === "Name" || key === "Label")
        ) {
          newChildProps[key] = `Copy ${childObj.currentJson[key]}`;
        } else {
          newChildProps[key] = childObj.currentJson[key];
          if (key === "CreatedAt") {
            newChildProps[key] = new Date().toISOString();
          }
          // Edge Case Coverage, (Other than Direct Parents)
          if (
            GRAND_RELATIONSHIPS.hasOwnProperty(childKind) &&
            GRAND_RELATIONSHIPS[childKind] === key
          ) {
            let oldId = childObj.currentJson[key];
            if (oldNewFieldMapping[oldId]) {
              newChildProps[key] = oldNewFieldMapping[oldId];
            }
          }
          // resetting old field / model Id with newly created field / model Id in phase two
          let referenceKey = manyToOneKeyList.find((refName) =>
            refName.includes(`${key}::`)
          );
          let referenceModelName = referenceKey
            ? referenceKey.split("::")[1]
            : "";
          if (
            //since obj['key'] & obj[[key]] are same,
            //checking specifically for newChildProps[key] to string
            // typeof newChildProps[key] === 'string' &&
            ["Field", "Model"].includes(referenceModelName) &&
            oldNewFieldMapping[newChildProps[key]]
          ) {
            newChildProps[key] = oldNewFieldMapping[newChildProps[key]];
          }
        }
      } else if (key === "Id") {
        //For Field & Child Table alone Id is generated from user given string, so generate it manually
        if (childKind === "Field") {
          let idFromName = getIdFromString(
            removeSpecialCharacters(childObj.currentJson["Name"] || "")
          );
          newChildProps[key] = childObj.getTruncatedFieldId(
            idFromName || childObj.getValue(key)
          );
        }
        if (childKind === "Model") {
          let clonedChildName = `Copy ${childObj.currentJson["Name"]}`;
          let idFromName = getIdFromString(clonedChildName);
          newChildProps[key] = childObj.getTruncatedModelId(
            idFromName || childObj.getValue(key)
          );
        }
      }
    });
    console.info(
      "** old and new childobj props",
      childObj.currentJson,
      newChildProps,
      oneToManyList
    );
    let newChildIns = this.create(childKind, newChildProps, refName);

    if (childKind === "Expression") {
      expressionList.push(newChildIns.id);
    }

    // update oldObject id with newly created modelId
    if (["Field", "Model"].includes(childKind)) {
      oldNewFieldMapping[childObj.id] = newChildIns.id;
    }
    if (
      Object.keys(PHASE_TWO_COPY_EXCLUDE_MAPPING).includes(childKind) ||
      Object.keys(PHASE_THREE_COPY_EXCLUDE_MAPPING).includes(childKind)
    ) {
      //keep the oldModel with newModel only if it is eligible for phase 2 copy
      oldNewModelInsMapping.set(childObj, newChildIns);
    }

    oneToManyList.forEach((key) => {
      let [refName, childName] = key.split("::");
      let children = childObj.getList(refName, childName);
      children.forEach((child) => {
        newChildIns.copyChild(child, refName);
      });
    });

    return newChildIns;
  }

  validateOneToMany() {
    var children = {};
    var oneToManyList = this.constructor.oneToManyList;
    for (let oneToManyName of oneToManyList) {
      let childIdList = this.currentJson[oneToManyName] || [];
      let childName = oneToManyName.split("::")[1];
      if (childIdList.length > 0) {
        children[childName] = [];

        childIdList.forEach((id) => {
          var obj = this.getObject(id);
          var objErrors = obj.validate && obj.validate();
          if (!isEmpty(objErrors)) {
            children[childName].push(objErrors);
          }
        });

        if (isEmpty(children[childName])) {
          // eslint-disable-next-line prefer-reflect
          delete children[childName];
        }
      }
    }

    return children;
  }

  validateProperties() {
    var errors = [];
    for (let name in this.constructor.fieldTypes) {
      if (this.constructor.fieldTypes[name].required) {
        if (typeof this.getValue(name) === "string" && !this.getValue(name)) {
          errors.push(name);
        } else if (
          typeof this.getValue(name) === "undefined" ||
          this.getValue(name) === null
        ) {
          errors.push(name);
        } else if (
          typeof this.getValue(name) === "object" &&
          isEmpty(this.getValue(name))
        ) {
          errors.push(name);
        }
      }
    }
    return errors;
  }

  commit() {
    this.ds.commit();
  }

  getCurrentModel() {
    return this.ds.current;
  }

  getIntactModel() {
    return this.ds.intact;
  }
}

BaseModel.setFieldTypes({
  Id: FieldTypes.string,
  Kind: FieldTypes.string,
  Name: FieldTypes.string,
  Type: FieldTypes.string
});

export { BaseModel };
